import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { useBuild } from '../contexts/BuildContext';

const AiLabelsSection = ({ onUpdate, boostInput = false }) => {
  const { fetchCachedList, selectedIndex } = useBuild();
  const [currentAiLabelPrefix, setCurrentAiLabelPrefix] = useState('');
  const [currentAiLabel, setCurrentAiLabel] = useState('');
  const [currentConfidence, setCurrentConfidence] = useState('med');
  const [boost, setBoost] = useState(1);
  const [aiLabelOptions, setAiLabelOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // AI Label prefixes and filtered labels
  const [aiLabelPrefixes, setAiLabelPrefixes] = useState([]);
  const [filteredAiLabels, setFilteredAiLabels] = useState([]);

  const confidenceOptions = [
    { id: 'low', label: 'Low' },
    { id: 'med', label: 'Medium' },
    { id: 'high', label: 'High' }
  ];

  // Fetch AI label options on component mount and when selectedIndex changes
  useEffect(() => {
    const fetchAiLabelOptions = async () => {
      try {
        setLoading(true);
        setError(null);
        const endpoint = `/search/frequent_values/${selectedIndex}/ai_labels_med`;
        const aiLabelsData = await fetchCachedList(endpoint);
        setAiLabelOptions(aiLabelsData);
      } catch (err) {
        console.error('Failed to fetch AI label options:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAiLabelOptions();
  }, [fetchCachedList, selectedIndex]);

  // Extract prefixes from AI labels and update filtered labels when data changes
  useEffect(() => {
    if (aiLabelOptions.length > 0) {
      const prefixes = [...new Set(aiLabelOptions.map(label => {
        const colonIndex = label.id.indexOf(':');
        return colonIndex > 0 ? label.id.substring(0, colonIndex) : 'Other';
      }))].sort();
      
      setAiLabelPrefixes(prefixes);
      
      // Set default prefix if none selected
      if (!currentAiLabelPrefix && prefixes.length > 0) {
        setCurrentAiLabelPrefix(prefixes[0]);
      }
    }
  }, [aiLabelOptions, currentAiLabelPrefix]);

  // Filter AI labels based on selected prefix
  useEffect(() => {
    if (currentAiLabelPrefix && aiLabelOptions.length > 0) {
      const filtered = aiLabelOptions.filter(label => {
        const colonIndex = label.id.indexOf(':');
        const prefix = colonIndex > 0 ? label.id.substring(0, colonIndex) : 'Other';
        return prefix === currentAiLabelPrefix;
      });
      setFilteredAiLabels(filtered);
      
      // Reset selected labels when prefix changes
      setCurrentAiLabel('');
    }
  }, [currentAiLabelPrefix, aiLabelOptions]);

  const handleAiLabelAction = (actionType) => {
    if (!currentAiLabel || !currentConfidence) return;
    
    const updateData = {
      action: actionType,
      filter: 'terms',
      field: `ai_labels_${currentConfidence}`,
      value: [currentAiLabel]
    };
    
    // Add boost value if boostInput is enabled
    if (boostInput) {
      updateData.boost = boost;
    }
    
    onUpdate(updateData);
    // Reset after adding
    setCurrentAiLabel('');
    setCurrentConfidence('med');
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  // Show error state
  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert severity="error">
          Failed to load AI labels: {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Prefix</InputLabel>
        <Select
          value={currentAiLabelPrefix}
          label="Prefix"
          onChange={(e) => setCurrentAiLabelPrefix(e.target.value)}
          disabled={loading}
        >
          {aiLabelPrefixes.map((prefix) => (
            <MenuItem key={prefix} value={prefix}>
              {prefix}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>AI Label</InputLabel>
        <Select
          value={currentAiLabel}
          label="AI Label"
          onChange={(e) => setCurrentAiLabel(e.target.value)}
          disabled={loading || !currentAiLabelPrefix}
        >
          {filteredAiLabels.map((label) => (
            <MenuItem key={label.id} value={label.id}>
              {label.id} ({label.count})
            </MenuItem>
          ))}
        </Select>
      </FormControl>
        
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Confidence</InputLabel>
        <Select
          value={currentConfidence}
          label="Confidence"
          onChange={(e) => setCurrentConfidence(e.target.value)}
          disabled={loading}
        >
          {confidenceOptions.map((option) => (
            <MenuItem key={option.id} value={option.id}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
        
      {boostInput && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Boost"
          type="number"
          value={boost}
          onChange={(e) => setBoost(parseFloat(e.target.value) || 1)}
          inputProps={{ min: 0, step: 0.1 }}
          disabled={loading}
        />
      )}
        
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleAiLabelAction('include')}
          disabled={!currentAiLabel || !currentConfidence || loading}
        >
          <AddIcon />
        </Button>
        
        {boostInput && (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleAiLabelAction('boost')}
            disabled={!currentAiLabel || !currentConfidence || loading}
          >
            <TrendingUpIcon />
          </Button>
        )}
        
        <Button
          variant="contained"
          color="error"
          onClick={() => handleAiLabelAction('exclude')}
          disabled={!currentAiLabel || !currentConfidence || loading}
        >
          <RemoveIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default AiLabelsSection;
